import React from 'react'
import { Button } from '../button'
import { Mic } from 'lucide-react'
import {
  RichTextInput,
  type SubmitPayload,
  type UploadedAttachment,
} from '../../Chat/components/RichTextInput/RichTextInput'
import { VoiceRecorder } from '../../Chat/components/VoiceRecorder/VoiceRecorder'

interface InputAreaProps {
  isRecording: boolean
  isTyping: boolean
  disabled?: boolean
  placeholder?: string
  resetKey?: number
  onSubmit: (payload: SubmitPayload) => void
  onStartRecording: () => void
  onVoiceMessage: (audioBlob: Blob, transcript?: string, durationSeconds?: number) => void
  onVoiceCancel: () => void
  onEndConversation: () => void
  uploadAdapter: (file: File) => Promise<UploadedAttachment>
}

// Dummy function to enable attachment button in RichTextInput
// The actual file handling is done internally by RichTextInput
const enableAttachments = () => {}

export function InputArea({
  isRecording,
  isTyping,
  disabled,
  placeholder,
  resetKey,
  onSubmit,
  onStartRecording,
  onVoiceMessage,
  onVoiceCancel,
  onEndConversation,
  uploadAdapter,
}: InputAreaProps) {
  return (
    <div className="border-t border-app-grey-30 bg-app-white-100 px-6 py-4 flex flex-col gap-3 rounded-b-xl">
      {isRecording && (
        <VoiceRecorder onVoiceMessage={onVoiceMessage} onCancel={onVoiceCancel} autoStart={true} />
      )}

      <RichTextInput
        key={resetKey}
        placeholder={
          placeholder || (isTyping ? 'Coach Logic is typing...' : 'Share your thoughts....')
        }
        onSubmit={onSubmit}
        onTextChange={() => {}}
        initialContent=""
        disabled={disabled || isRecording || isTyping}
        uploadAdapter={uploadAdapter}
        onAttachClick={enableAttachments}
        rightActions={
          <>
            {!isRecording && (
              <button
                type="button"
                onClick={onStartRecording}
                disabled={isTyping}
                className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors border-none bg-transparent ${
                  isTyping
                    ? 'text-app-grey-30 cursor-not-allowed'
                    : 'text-app-grey-50 hover:text-app-grey-70 hover:bg-app-grey-20 cursor-pointer'
                }`}
                title={isTyping ? 'Wait for response' : 'Record voice message'}
              >
                <Mic className="w-5 h-5" />
              </button>
            )}
          </>
        }
      />

      <div className="flex items-center justify-between">
        <p className="text-xs text-app-grey-50 m-0">
          Press Enter to send, Shift+Enter for new line
        </p>
        <Button variant="outline" className="border border-app-grey-30" onClick={onEndConversation}>
          End Conversation
        </Button>
      </div>
    </div>
  )
}
